import { Injectable } from '@angular/core';
import { LMToolbarServiceBase } from '@toolbar/lm-toolbar-service.base';
import { AppConstants } from '@utils/app.constants';
import { LMToolbarServiceAdapter } from '@toolbar/lm-toolbar-service-adapter';
import { LMContextService } from '@services/lm-context.service';
import { LMPageSpecUtilService } from './lm-page-spec-util.service';

@Injectable()
export class LMPageToolbarService extends LMToolbarServiceBase {

  constructor(
    private lmContextService: LMContextService,
    private lmPageSpecUtilService: LMPageSpecUtilService,
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter
  ) {
    super(lmToolbarServiceAdapter); // must msg the base class constructor
  }

  public buildToolbarButtons(pageSpec: LMPageSpec, pageStateInfo: PageStateInfo): void {
    this.updateToolbar(pageSpec, pageStateInfo);
  }

  public updateToolbar(pageSpec: LMPageSpec, pageStateInfo: PageStateInfo) {
    const actionRefs: LMBusActionReference[] = this.getPageToolbarActionRefs(pageSpec);
    this.toolbarButtons = this.pageActionRefArrayToToolbarButtonArray(pageSpec, actionRefs);
    this._updateToolbarState(this.toolbarButtons, pageSpec, pageStateInfo);
    this.fireToolbarObservable();
  }

  private getPageToolbarActionRefs(pageSpec: LMPageSpec): LMBusActionReference[] {
    const context = this.lmContextService.getContext();
    const noPdfInConfig = context.configuredFeatures.indexOf(AppConstants.FEATURE_ExportToPDF) !== -1;
    const noPagePdf = context.hidePagePdf;  // config parameter to revert pagePDF feature
    const actionReferenceArray = [];

    if (!noPagePdf && (!context.disallowExport && noPdfInConfig && this.displayPDFExportButton(pageSpec))) {
      actionReferenceArray.push(this.getStandardToolbarActions().printToPDF);
    }

    if (this.lmPageSpecUtilService.allowUpdate(pageSpec)) {
      actionReferenceArray.push(this.getStandardToolbarActions().save);
    }

    actionReferenceArray.push(this.getStandardToolbarActions().refresh);

    return actionReferenceArray;
  }

  private pageActionRefArrayToToolbarButtonArray(pageSpec: LMPageSpec, actionRefArray: LMBusActionReference[]): LMToolbarButton[] {
    return actionRefArray.map(actionRef =>
      this.buildLMToolbarButton(pageSpec, actionRef, true, false, false));
  }

  // If it is only MenuPanel do not show the PDF
  private displayPDFExportButton(pageSpec: LMPageSpec): boolean {

    if (pageSpec.pageSpec?.panels?.length === 1
      && (pageSpec.pageSpec?.panels[0]?.pane?.paneType === 'MenuPanel'
      || pageSpec.pageSpec?.panels[0]?.pane?.paneType === 'menuPanel')) {
      return false;
    }

    return true;
  }

  // Checks if panel is supported for PDF export
  private isPdfExportSupported(panel: LMPagePanelSpec): boolean {
    if (!panel) {
      return false;
    }

    if (panel.panelType === 'MultiPanePanel') {
      return true;
    }

    if (panel.panelType === 'Panel' && panel.pane.listSpec) {
      return !panel.pane.listSpec.busListSpec?.implementsOrgChartView;
    }

    if (panel.panelType === 'DashBoardPanel') {
      for (const pane of panel.panes) {
        if (!pane.listSpec) {
          return false;
        }

        if (pane.listSpec.busListSpec?.implementsOrgChartView) {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  private _updateToolbarState(toolbarButtons: LMToolbarButton[], pageSpec: LMPageSpec, pageStateInfo: PageStateInfo) {
    toolbarButtons.forEach((toolbarButton: LMToolbarButton) => {
      if (toolbarButton.actionReference.id === AppConstants.STANDARD_SAVE) {
        toolbarButton.state.disabled = !pageStateInfo.isModified;
      }
      // Disables PDF button for unsupported types
      if (toolbarButton.actionReference.id === AppConstants.STANDARD_TOOLBAR_EXPORTPDF) {
        toolbarButton.state.disabled = !this.isPdfExportSupported(pageStateInfo.selectedPanel);
      }
    });
  }
}
